/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import clsx from 'clsx'
import {useTranslation} from 'react-i18next'
import UiFormInner from 'components/Forms/UIFormInner'
import CompanyBasicForm from 'components/Companies/forms/CompanyBasicForm'
import UiButton, {UiButtonSize, UiButtonStyle, UiButtonType} from 'components/Ui/UiButton'
import {PlusIcon} from '@heroicons/react/24/solid'
import {UiTable, UiTableBody, UiTableContainer, UiTableHead, UiTableHeaderCell} from 'components/Ui/UiTable'
import EditCompanyModalAddressList from './EditCompanyModalAddressList'
import EditCompanyModalContactList from './EditCompanyModalContactList'
import {CompanyAddress} from 'types/entities/CompanyAddress'
import {CompanyContact} from 'types/entities/CompanyContact'
import {Company} from 'types/entities/Company'

interface EditCompanyModalCompanyTabProps {
    selectedCompany: Company
    onChangeCompany: (shouldClose?: boolean) => void
    onAddAddress: () => void
    onAddContact: () => void
    onEditAddress: (address?: CompanyAddress) => void
    onDeleteAddress: (address: CompanyAddress) => Promise<void>
    onEditContact: (contact?: CompanyContact) => void
    onDeleteContact: (contact: CompanyContact) => Promise<void>
    onViewContact: (contact: CompanyContact) => void
}

const EditCompanyModalCompanyTab = ({
    selectedCompany,
    onChangeCompany,
    onAddAddress,
    onAddContact,
    onEditAddress,
    onDeleteAddress,
    onEditContact,
    onDeleteContact,
    onViewContact,
}: EditCompanyModalCompanyTabProps) => {
    const { t } = useTranslation()

    return (
        <div className={clsx('EditCompanyModalCompanyTab col-span-12 grid grid-cols-12 gap-3')}>
            <div className={clsx('EditCompanyModalCompanyTabBasic col-span-12 xl:col-span-4 2xl:col-span-3 flex flex-col pt-4')}>
                <UiFormInner title={t('Basic Information')}>
                    <CompanyBasicForm 
                      compact={true} 
                                            onChange={(_company: Company, shouldClose?: boolean) => onChangeCompany(shouldClose)}
                      showSaveAndClose={true} 
                      isInEditModal={true} 
                    />
                </UiFormInner>
            </div>

            <div className={clsx('EditCompanyModalCompanyTabAddresses col-span-12 xl:col-span-4 2xl:col-span-5 flex flex-col pt-4')}>
                <UiFormInner
                    title={t('Addresses')}
                    actions={
                        <UiButton
                            onClick={onAddAddress}
                            style={UiButtonStyle.Primary}
                            size={UiButtonSize.Small}
                            type={UiButtonType.Button}
                            className="p-0! h-8 w-8"
                            icon={<PlusIcon className={clsx('w-5 h-5')} />}
                        />
                    }
                >
                    <UiTableContainer>
                        <UiTable>
                            <UiTableHead>
                                <UiTableHeaderCell align="left">{t('Name')}</UiTableHeaderCell>
                                <UiTableHeaderCell align="left">{t('Department')}</UiTableHeaderCell>
                                <UiTableHeaderCell align="left">{t('Street')}</UiTableHeaderCell>
                                <UiTableHeaderCell align="left">{t('City')}</UiTableHeaderCell>
                                <UiTableHeaderCell align="left">{t('Country')}</UiTableHeaderCell>
                                <UiTableHeaderCell align="left">{t('Note')}</UiTableHeaderCell>
                                <UiTableHeaderCell align="right"></UiTableHeaderCell>
                            </UiTableHead>
                            <UiTableBody>
                                <EditCompanyModalAddressList
                                    addresses={selectedCompany.addresses || []}
                                    onEdit={onEditAddress}
                                    onDelete={onDeleteAddress}
                                />
                            </UiTableBody>
                        </UiTable>
                    </UiTableContainer>
                </UiFormInner>
            </div>

            <div className={clsx('EditCompanyModalCompanyTabContacts col-span-12 xl:col-span-4 2xl:col-span-4 flex flex-col pt-4')}>
                <UiFormInner
                    title={t('Contact Persons')}
                    actions={
                        <UiButton
                            onClick={onAddContact}
                            style={UiButtonStyle.Primary}
                            size={UiButtonSize.Small}
                            type={UiButtonType.Button}
                            className="p-0! h-8 w-8"
                            icon={<PlusIcon className={clsx('w-5 h-5')} />}
                        />
                    }
                >
                    <UiTableContainer>
                        <UiTable>
                            <UiTableHead>
                                <UiTableHeaderCell align="left">{t('Name')}</UiTableHeaderCell>
                                <UiTableHeaderCell align="left">{t('Email')}</UiTableHeaderCell>
                                <UiTableHeaderCell align="left">{t('Phone')}</UiTableHeaderCell>
                                <UiTableHeaderCell align="right"></UiTableHeaderCell>
                            </UiTableHead>
                            <UiTableBody>
                                <EditCompanyModalContactList
                                    contacts={selectedCompany.contacts || []}
                                    onView={onViewContact}
                                    onEdit={onEditContact}
                                    onDelete={onDeleteContact}
                                />
                            </UiTableBody>
                        </UiTable>
                    </UiTableContainer>
                </UiFormInner>
            </div>
        </div>
    )
}

export default EditCompanyModalCompanyTab
